import React, { useEffect, useMemo, useState } from 'react'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import StickyLanguageSelector from '../../components/StickyLanguageSelector'
import { getAll, putOne } from '../../services/common_requests'
import { KTCardBody } from '../../../_dashboard/helpers'
import { MENU_NAV_LANGUAGES } from '../header-management/menuNavigationUtils'

type LegalLang = (typeof MENU_NAV_LANGUAGES)[number]
type LegalLangMap = Record<LegalLang, string>

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

function readInitialLang(): LegalLang {
  try {
    const ls = localStorage.getItem(I18N_CONFIG_KEY) || '{"selectedLang":"fr"}'
    const code = JSON.parse(ls).selectedLang as string
    return (MENU_NAV_LANGUAGES as readonly string[]).includes(code) ? (code as LegalLang) : 'en'
  } catch {
    return 'en'
  }
}

function emptyLangMap(): LegalLangMap {
  return { en: '', fr: '', de: '' }
}

function defaultPrivacyPolicyPaths(): LegalLangMap {
  return { en: 'privacy-policy', fr: 'privacy-policy', de: 'privacy-policy' }
}

function mergeLangRecord(api: Record<string, string> | undefined | null): LegalLangMap {
  const o = emptyLangMap()
  if (!api) return o
  for (const k of MENU_NAV_LANGUAGES) {
    if (api[k] != null) o[k] = api[k]
  }
  return o
}

function extractStandardData(res: any): any {
  return res?.data ?? null
}

const LegalManagement: React.FC = () => {
  const intl = useIntl()
  const [selectedLanguage, setSelectedLanguage] = useState<LegalLang>(readInitialLang)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const validationSchema = useMemo(
    () =>
      Yup.object({
        cookieBannerTitle: Yup.object(),
        cookieBannerHtml: Yup.object(),
        cookieAcceptLabel: Yup.object(),
        cookieDeclineLabel: Yup.object(),
        privacyPolicyPath: Yup.object(),
      }),
    [],
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cookieBannerTitle: emptyLangMap(),
      cookieBannerHtml: emptyLangMap(),
      cookieAcceptLabel: emptyLangMap(),
      cookieDeclineLabel: emptyLangMap(),
      privacyPolicyPath: defaultPrivacyPolicyPaths(),
    },
    validationSchema,
    onSubmit: async (values) => {
      setSaving(true)
      try {
        const res = await putOne(
          {
            cookieBannerTitle: values.cookieBannerTitle,
            cookieBannerHtml: values.cookieBannerHtml,
            cookieAcceptLabel: values.cookieAcceptLabel,
            cookieDeclineLabel: values.cookieDeclineLabel,
            privacyPolicyPath: values.privacyPolicyPath,
          },
          '/site-settings/legal',
        )
        if ((res as any)?.payload?.code === 3013) {
          toast.success(intl.formatMessage({ id: 'LEGAL_MANAGEMENT.SUCCESS.SAVED' }), {
            position: 'top-right',
          })
        } else {
          toast.error(intl.formatMessage({ id: 'LEGAL_MANAGEMENT.ERROR.SAVE' }))
        }
      } catch {
        toast.error(intl.formatMessage({ id: 'LEGAL_MANAGEMENT.ERROR.SAVE' }))
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await getAll({}, '/site-settings/legal')
        const d = extractStandardData(res)
        if (!d || cancelled) return
        formik.setValues({
          cookieBannerTitle: mergeLangRecord(d.cookieBannerTitle),
          cookieBannerHtml: mergeLangRecord(d.cookieBannerHtml),
          cookieAcceptLabel: mergeLangRecord(d.cookieAcceptLabel),
          cookieDeclineLabel: mergeLangRecord(d.cookieDeclineLabel),
          privacyPolicyPath: mergeLangRecord(d.privacyPolicyPath),
        })
      } catch {
        toast.error(intl.formatMessage({ id: 'LEGAL_MANAGEMENT.ERROR.LOAD' }))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [])

  if (loading) {
    return (
      <KTCardBody className='py-15 text-center'>
        <Spinner animation='border' className='text-primary' />
      </KTCardBody>
    )
  }

  return (
    <KTCardBody className='py-6 px-4 px-lg-8'>
      <div className='d-flex flex-column mb-8'>
        <h1 className='fs-2x fw-bold text-gray-900 mb-1'>
          {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.PAGE_TITLE' })}
        </h1>
        <p className='text-gray-500 fs-6 mb-0'>
          {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.PAGE_SUBTITLE' })}
        </p>
      </div>

      <StickyLanguageSelector
        selectedLanguage={selectedLanguage}
        onLanguageChange={(lang) => {
          if ((MENU_NAV_LANGUAGES as readonly string[]).includes(lang)) {
            setSelectedLanguage(lang as LegalLang)
          }
        }}
        allowedLanguageCodes={[...MENU_NAV_LANGUAGES]}
      />

      <form onSubmit={formik.handleSubmit}>
        <div className='card card-flush shadow-sm mb-6'>
          <div className='card-header border-0 pt-6'>
            <span className='fw-bold fs-3 text-gray-900'>
              {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.SECTION.BANNER' })}
            </span>
          </div>
          <div className='card-body pt-0 pb-6 px-6'>
            <Form.Group className='mb-6'>
              <Form.Label className='fw-semibold text-gray-700'>
                {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.TITLE' })}{' '}
                <span className='badge badge-light-primary ms-1'>
                  {selectedLanguage.toUpperCase()}
                </span>
              </Form.Label>
              <Form.Control
                className='form-control-solid'
                value={formik.values.cookieBannerTitle[selectedLanguage] || ''}
                onChange={(e) =>
                  formik.setFieldValue(`cookieBannerTitle.${selectedLanguage}`, e.target.value)
                }
              />
            </Form.Group>
            <Form.Group className='mb-6'>
              <Form.Label className='fw-semibold text-gray-700'>
                {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.BODY_HTML' })}{' '}
                <span className='badge badge-light-primary ms-1'>
                  {selectedLanguage.toUpperCase()}
                </span>
              </Form.Label>
              <div className='rounded border border-gray-200 overflow-hidden'>
                <ReactQuill
                  theme='snow'
                  value={formik.values.cookieBannerHtml[selectedLanguage] || ''}
                  onChange={(html) =>
                    formik.setFieldValue(`cookieBannerHtml.${selectedLanguage}`, html)
                  }
                />
              </div>
            </Form.Group>
            <Row className='g-6'>
              <Col md={6}>
                <Form.Group className='mb-0'>
                  <Form.Label className='fw-semibold text-gray-700'>
                    {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.ACCEPT_LABEL' })}{' '}
                    <span className='badge badge-light-primary ms-1'>
                      {selectedLanguage.toUpperCase()}
                    </span>
                  </Form.Label>
                  <Form.Control
                    className='form-control-solid'
                    value={formik.values.cookieAcceptLabel[selectedLanguage] || ''}
                    onChange={(e) =>
                      formik.setFieldValue(`cookieAcceptLabel.${selectedLanguage}`, e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-0'>
                  <Form.Label className='fw-semibold text-gray-700'>
                    {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.DECLINE_LABEL' })}{' '}
                    <span className='badge badge-light-primary ms-1'>
                      {selectedLanguage.toUpperCase()}
                    </span>
                  </Form.Label>
                  <Form.Control
                    className='form-control-solid'
                    value={formik.values.cookieDeclineLabel[selectedLanguage] || ''}
                    onChange={(e) =>
                      formik.setFieldValue(`cookieDeclineLabel.${selectedLanguage}`, e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        <div className='card card-flush shadow-sm mb-6'>
          <div className='card-header border-0 pt-6'>
            <span className='fw-bold fs-3 text-gray-900'>
              {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.SECTION.PRIVACY' })}
            </span>
          </div>
          <div className='card-body pt-0 pb-6 px-6'>
            <Form.Group className='mb-0'>
              <Form.Label className='fw-semibold text-gray-700'>
                {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.PRIVACY_PATH' })}{' '}
                <span className='badge badge-light-primary ms-1'>
                  {selectedLanguage.toUpperCase()}
                </span>
              </Form.Label>
              <Form.Control
                className='form-control-solid'
                placeholder='privacy-policy'
                value={formik.values.privacyPolicyPath[selectedLanguage] || ''}
                onChange={(e) =>
                  formik.setFieldValue(`privacyPolicyPath.${selectedLanguage}`, e.target.value)
                }
              />
              <div className='form-text'>
                {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.PRIVACY_PATH_HINT' })}
              </div>
            </Form.Group>
          </div>
        </div>

        <div className='d-flex justify-content-end gap-3'>
          <Button
            type='submit'
            variant='primary'
            className='px-8 py-3 rounded-pill shadow-sm d-flex align-items-center gap-2'
            disabled={saving}
          >
            {saving && <Spinner animation='border' size='sm' />}
            {intl.formatMessage({ id: 'LEGAL_MANAGEMENT.SAVE' })}
          </Button>
        </div>
      </form>
    </KTCardBody>
  )
}

export default LegalManagement
